import { Alert, Badge, Card, Group, Stack, Text, Tooltip } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { AlertMarkers, ForwardKeyBadge, ForwardOutcomeBadge } from "@/components/AlertMarkers";
import { DataTable } from "@/components/DataTable";
import { GenerationBadge } from "@/components/GenerationBadge";
import { Money } from "@/components/Money";
import { RelatedLink } from "@/components/RelatedLink";
import { StatusBadge } from "@/components/StatusBadge";
import { Timestamp } from "@/components/Timestamp";
import { EmptyState } from "@/components/states";
import { col } from "@/entities/columns";
import { COLLECTIONS } from "@/firestore/collections";
import { DOCUMENT_ID, useFirestoreInQuery, useFirestoreQuery } from "@/firestore/hooks";
import type {
  ResidualForwardDoc,
  SessionKeyDoc,
  WalletAvailabilityDoc,
  WalletDoc,
  WalletKeyInventoryDoc,
  WalletMigrationDoc,
} from "@/firestore/types";
import { isPermissionError } from "@/lib/errors";
import { formatMicroUsdc } from "@/lib/format";
import { useNow } from "@/lib/useNow";
import { entityPath } from "@/routes/paths";
import {
  ACCOUNT_ROLES,
  PREVIOUS_WALLET_STATES,
  RESIDUAL_FLOOR_MICRO_USDC,
  liveForwardRoles,
  residualCandidates,
  selectResidualWallets,
} from "@/wallet/residuals";
import type { ResidualWalletRow } from "@/wallet/residuals";

/** Previous wallets read per page load; a fleet larger than this is flagged. */
const PREVIOUS_LIMIT = 500;

function columns() {
  return [
    col<ResidualWalletRow>({
      id: "wallet",
      header: "Previous wallet",
      value: (r) => r.id,
      cell: (r) => (
        <Group gap={4} wrap="nowrap">
          <RelatedLink entity="wallet" id={r.id} />
          <StatusBadge state={r.state} />
        </Group>
      ),
    }),
    col<ResidualWalletRow>({
      id: "residual",
      header: "Residual",
      value: (r) => r.totalMicroUsdc,
      cell: (r) => <Money micro={r.totalMicroUsdc} size="sm" fw={700} />,
    }),
    col<ResidualWalletRow>({
      id: "main",
      header: "Main",
      value: (r) => r.mainMicroUsdc,
      cell: (r) => <Money micro={r.mainMicroUsdc} size="sm" />,
    }),
    col<ResidualWalletRow>({
      id: "reserve",
      header: "Reserve",
      value: (r) => r.reserveMicroUsdc,
      cell: (r) => <Money micro={r.reserveMicroUsdc} size="sm" />,
    }),
    col<ResidualWalletRow>({
      id: "since",
      header: "Residual since",
      value: (r) => r.since ?? "",
      cell: (r) => (
        <Tooltip
          label="When the balance last changed (walletAvailability's recompute): the residual has sat at least since then."
          multiline
          w={280}
          withArrow
        >
          <span>
            <Timestamp value={r.since} />
          </span>
        </Tooltip>
      ),
    }),
    ...ACCOUNT_ROLES.map((role) =>
      col<ResidualWalletRow>({
        id: `${role}Key`,
        header: `${role === "main" ? "Main" : "Reserve"} forward key`,
        value: (r) => r.keys[role].status,
        cell: (r) => <ForwardKeyBadge state={r.keys[role]} withExpiry />,
      }),
    ),
    col<ResidualWalletRow>({
      id: "latest",
      header: "Latest forwards",
      value: (r) =>
        ACCOUNT_ROLES.map((role) => r.latest[role]?.createdAt ?? "")
          .sort()
          .pop() ?? "",
      cell: (r) => {
        const entries = ACCOUNT_ROLES.flatMap((role) => {
          const forward = r.latest[role];
          return forward ? [{ role, forward }] : [];
        });
        if (entries.length === 0) return <Text c="dimmed">none</Text>;
        return (
          <Stack gap={2}>
            {entries.map(({ role, forward }) => (
              <Group key={role} gap={4} wrap="nowrap">
                <Text size="xs" tt="capitalize" w={52}>
                  {role}
                </Text>
                <ForwardOutcomeBadge forward={forward} />
                <Timestamp value={forward.createdAt} />
              </Group>
            ))}
          </Stack>
        );
      },
    }),
    col<ResidualWalletRow>({
      id: "alerts",
      header: "Alerts",
      value: (r) => r.alerts.map((a) => a.kind).join(" "),
      cell: (r) =>
        r.alerts.length ? <AlertMarkers markers={r.alerts} /> : <Text c="dimmed">—</Text>,
    }),
    col<ResidualWalletRow>({
      id: "migration",
      header: "Migration",
      value: (r) => r.migration?.completedAt ?? "",
      cell: (r) =>
        r.migration ? (
          <Stack gap={0}>
            <Group gap={4} wrap="nowrap">
              <RelatedLink entity="walletMigration" id={r.migration._docId} />
              <StatusBadge state={r.migration.state} />
            </Group>
            {r.migration.completedAt ? (
              <Text size="xs" c="dimmed">
                completed <Timestamp value={r.migration.completedAt} />
              </Text>
            ) : null}
          </Stack>
        ) : (
          <Text c="dimmed">—</Text>
        ),
    }),
    col<ResidualWalletRow>({
      id: "successor",
      header: "Successor",
      value: (r) => r.successor ?? "",
      cell: (r) => <RelatedLink entity="wallet" id={r.successor} />,
    }),
    col<ResidualWalletRow>({
      id: "generation",
      header: "Generation",
      value: (r) => r.wallet.contractGeneration ?? "",
      cell: (r) => <GenerationBadge generation={r.wallet.contractGeneration} />,
    }),
    col<ResidualWalletRow>({
      id: "owner",
      header: "User",
      value: (r) => r.owner,
      cell: (r) => <RelatedLink entity="user" id={r.owner} />,
    }),
  ];
}

const COLUMNS = columns();

/**
 * Old-wallet residuals (AD-1c): every `migrated` or `retired` wallet whose cached balance (Main +
 * Reserve) is at or above the 0.10 USDC floor, largest and oldest first, with each account's forward
 * key, its latest forwards and the alert markers they imply. Open a row for Wallet 360, where the
 * Previous wallets panel has [Forward now] and [Re-send Move prompt].
 *
 * Reads: `wallets where state in [migrated, retired]` (single-field), then, by `in` batches of 30,
 * their `walletAvailability`, and for the candidates their `residualForwards` (`wallet`),
 * `walletMigrations` (`oldWallet`), `sessionKeys` (`account`) and `walletKeyInventories` (closed?).
 */
export function OldWalletResidualsPage() {
  const navigate = useNavigate();
  const now = useNow();
  const wallets = useFirestoreQuery<WalletDoc>(COLLECTIONS.wallets, {
    filters: [["state", "in", [...PREVIOUS_WALLET_STATES]]],
    limit: PREVIOUS_LIMIT,
  });
  const availabilities = useFirestoreInQuery<WalletAvailabilityDoc>(
    COLLECTIONS.walletAvailability,
    DOCUMENT_ID,
    wallets.data.map((w) => w._docId),
  );
  const candidateIds = residualCandidates(wallets.data, availabilities.data).map(
    (c) => c.wallet._docId,
  );
  const forwards = useFirestoreInQuery<ResidualForwardDoc>(
    COLLECTIONS.residualForwards,
    "wallet",
    candidateIds,
  );
  const migrations = useFirestoreInQuery<WalletMigrationDoc>(
    COLLECTIONS.walletMigrations,
    "oldWallet",
    candidateIds,
  );
  const keys = useFirestoreInQuery<SessionKeyDoc>(COLLECTIONS.sessionKeys, "account", candidateIds);
  const inventories = useFirestoreInQuery<WalletKeyInventoryDoc>(
    COLLECTIONS.walletKeyInventories,
    DOCUMENT_ID,
    candidateIds,
  );

  const rows = selectResidualWallets({
    wallets: wallets.data,
    availabilities: availabilities.data,
    migrations: migrations.data,
    keys: keys.data,
    forwards: forwards.data,
    inventories: inventories.data,
    now,
  });
  const total = rows.reduce((sum, r) => sum + r.totalMicroUsdc, 0);
  const forwardable = rows.filter((r) => liveForwardRoles(r.keys).length > 0).length;
  const alerting = rows.filter((r) => r.alerts.length > 0).length;
  const loading =
    wallets.loading ||
    availabilities.loading ||
    forwards.loading ||
    migrations.loading ||
    keys.loading;
  const failures = [
    ["Previous wallets", wallets.error],
    ["Balances", availabilities.error],
    ["Forwards", forwards.error],
    ["Migrations", migrations.error],
    ["Session keys", keys.error],
    // The key inventory only tells whether a wallet is closed; before 0.74.0 it is not readable.
    [
      "Key inventories (closed wallets)",
      inventories.error && !isPermissionError(inventories.error) ? inventories.error : null,
    ],
  ].filter((entry): entry is [string, Error] => entry[1] instanceof Error);

  return (
    <>
      <PageHeader
        title="Old-wallet residuals"
        subtitle={`Migrated or retired wallets still holding ${formatMicroUsdc(RESIDUAL_FLOOR_MICRO_USDC)} or more: money that reached an old wallet after its migration and was not forwarded yet.`}
      />
      <Card withBorder radius="md" padding="md">
        <Stack gap="sm">
          <Text size="sm" c="dimmed" maw={900}>
            A credit of a previous wallet is forwarded to the successor with the migration&apos;s
            forward keys for 90 days after completion (automatic triggers skip a balance below the
            floor), then only the owner&apos;s one-tap Move moves it. A row here with a live key
            needs a look at its forwards (Forward now); one with no live key needs the owner
            (Re-send Move prompt). Both commands are on the wallet&apos;s Wallet 360, Previous
            wallets panel.
          </Text>
          {failures.length > 0 ? (
            <Alert color="red" variant="light" title="This list is incomplete">
              {failures.map(([label, error]) => (
                <Text key={label} size="sm">
                  {label}: {error.message}
                </Text>
              ))}
            </Alert>
          ) : null}
          {wallets.data.length >= PREVIOUS_LIMIT ? (
            <Alert color="orange" variant="light">
              Only the first {PREVIOUS_LIMIT} previous wallets were read: more exist, and this list
              may miss some.
            </Alert>
          ) : null}
          <Group gap="xs">
            <Badge variant="light" color="gray" radius="sm" tt="none">
              {wallets.data.length} previous wallet(s) read
            </Badge>
            <Badge variant={rows.length ? "filled" : "light"} color="orange" radius="sm" tt="none">
              {rows.length} with a residual: {formatMicroUsdc(total)}
            </Badge>
            <Badge variant="light" color="green" radius="sm" tt="none">
              {forwardable} with a live forward key
            </Badge>
            <Badge variant={alerting ? "filled" : "light"} color="red" radius="sm" tt="none">
              {alerting} with an alert
            </Badge>
          </Group>
          <DataTable
            data={rows}
            columns={COLUMNS}
            loading={loading}
            searchPlaceholder="Filter wallets…"
            onRowClick={(row) => navigate(entityPath("wallet", row.id))}
            emptyState={
              <EmptyState
                title="No residuals"
                description="No migrated or retired wallet holds 0.10 USDC or more. New ones appear here live."
              />
            }
          />
        </Stack>
      </Card>
    </>
  );
}
